import {GraphQLClient} from "graphql-request";
import {doAuthenticatedQuery} from "src/services/shared/doQuery";
import {portalUserRoles} from "../../utils/constants";
import {graphQLClient} from "../shared/GraphQLService";
import {
  saasClientRestrictionsQuery,
  setSaasClientRestrictionMutation,
  setSaasDisabledMutation,
} from "./SaasRestrictionsGraphQL";

// A SaaS and the clients it is shown to. A SaaS that is not
// restricted is a regular integration, available to all clients;
// a restricted one is a "custom" integration.
export interface ISaasClientRestriction {
  identifier: string;   // cfg.saas.name
  name: string;         // cfg.saas.description
  isDisabled: boolean;
  restricted: boolean;
  clientIds: string[];  // client pub_ids
}

// As returned by the saas_client_restrictions action
interface ISaasClientRestrictionResponse {
  name: string;
  description: string;
  is_disabled: boolean;
  restricted: boolean;
  client_ids?: string[] | null;
}

export const transformSaasClientRestriction = (
  r: ISaasClientRestrictionResponse
): ISaasClientRestriction => ({
  identifier: r.name,
  name: r.description,
  isDisabled: r.is_disabled,
  restricted: r.restricted,
  clientIds: r.client_ids ?? [],
});

// Super admin only
export const getSaasClientRestrictions = async ({accessToken, userRole}: {
    accessToken: string,
    userRole: string
  }
): Promise<ISaasClientRestriction[]> => {
  const theQuery = async (client: GraphQLClient) => {
    const result = client.request(
      saasClientRestrictionsQuery,
      {},
      {
        'x-hasura-role': userRole ?? portalUserRoles.user,
      },
    );
    return result;
  }
  let response;
  try {
    response = await doAuthenticatedQuery(
      theQuery, accessToken
    );
  }
  catch (error) {
    console.error('getSaasClientRestrictions failed')
    throw (error)
  }

  return response.saas_client_restrictions.map(transformSaasClientRestriction);
}

export const setSaasClientRestrictionVariables = ({
  saas,
  restricted,
  clientIds,
}: {
  saas: string,
  restricted: boolean,
  clientIds: string[],
}) => ({
  saas,
  restricted,
  // An unrestricted SaaS has no clients
  client_ids: restricted ? clientIds : [],
});

// Super admin only
export const setSaasClientRestriction = async (
  parameters: {
    hasuraAccessToken: string,
    saas: string,
    restricted: boolean,
    clientIds: string[],
    role?: string,
  },
) => {
  const {
    hasuraAccessToken,
    saas,
    restricted,
    clientIds,
    role
  } = parameters;

  if (!saas) throw new Error('saas required');
  if (restricted && !clientIds?.length) {
    throw new Error('at least one client required');
  }

  const gqlClient = await graphQLClient({accessToken: hasuraAccessToken});

  // Only super admins may set restrictions
  if (role === portalUserRoles.admin) {
    gqlClient.setHeader("x-hasura-role", role);
  }

  const result = await gqlClient?.request(
    setSaasClientRestrictionMutation,
    setSaasClientRestrictionVariables({saas, restricted, clientIds})
  )
  return result;
}

// Super admin only: enable or disable a SaaS for every client
export const setSaasDisabled = async (
  parameters: {
    hasuraAccessToken: string,
    saas: string,
    isDisabled: boolean,
    role?: string,
  },
) => {
  const {
    hasuraAccessToken,
    saas,
    isDisabled,
    role
  } = parameters;

  if (!saas) throw new Error('saas required');

  const gqlClient = await graphQLClient({accessToken: hasuraAccessToken});

  // Only super admins may enable or disable a SaaS
  if (role === portalUserRoles.admin) {
    gqlClient.setHeader("x-hasura-role", role);
  }

  const result = await gqlClient?.request(
    setSaasDisabledMutation,
    {saas, is_disabled: isDisabled}
  )
  return result;
}
